/*
 * Platform-admin Administration surface — mirrors the workspace
 * Administration shell but with the platform-operator categories
 * (Identity, Monetisation, DHIS2, Operations, Settings). Mounted at
 * /admin/administration so platform operators get the same focused
 * configuration experience as workspace owners.
 */

import { Navigate, Route, Routes } from "react-router-dom";
import { AuthSession } from "../lib/api.js";
import { usePlatformPermissions } from "../lib/usePermissions.js";
import AdministrationShell, { AdminCategory } from "./AdministrationShell.js";

import AdminMembers from "../pages/admin/AdminMembers.js";
import AdminRoles from "../pages/admin/AdminRoles.js";
import AdminUserRoles from "../pages/admin/AdminUserRoles.js";
import AdminPlans from "../pages/admin/AdminPlans.js";
import AdminPaymentGateways from "../pages/admin/AdminPaymentGateways.js";
import AdminBilling from "../pages/admin/AdminBilling.js";
import AdminInstances from "../pages/admin/AdminInstances.js";
import DhisInstances from "../pages/DhisInstances.js";
import AdminAppClients from "../pages/admin/AdminAppClients.js";
import AdminForwardingOversight from "../pages/admin/AdminForwardingOversight.js";
import AdminSecrets from "../pages/admin/AdminSecrets.js";
import AdminAudit from "../pages/admin/AdminAudit.js";
import AdminFeatureFlags from "../pages/admin/AdminFeatureFlags.js";
import AdminSecurityCenter from "../pages/admin/AdminSecurityCenter.js";
import AdminSupportConsole from "../pages/admin/AdminSupportConsole.js";
import AdminHealth from "../pages/admin/AdminHealth.js";
import AdminSettings from "../pages/admin/AdminSettings.js";
import ComingSoon from "../pages/ComingSoon.js";

const BASE = "/admin/administration";

export default function PlatformAdministrationArea({ session }: { session: AuthSession }) {
  const { has, hasAny } = usePlatformPermissions();
  const canUsers = hasAny("platform.users.view", "platform.users.manage");
  const canRoles = hasAny("platform.roles.view", "platform.roles.manage");
  const canAssignRoles = has("platform.roles.assign");
  const canPlans = hasAny("platform.plans.view", "platform.plans.manage");
  const canPayments = hasAny("platform.payments.view", "platform.payments.manage");
  const canBilling = hasAny("platform.billing.view", "platform.billing.manage", "platform.refunds.manage");
  const canIntegrations = hasAny("platform.tenants.view", "platform.connections.diagnose");
  const canSecrets = hasAny("platform.secrets.status.view", "platform.security.view");
  const canAudit = has("platform.audit.view");
  const canFeatureFlags = hasAny("platform.feature_flags.view", "platform.feature_flags.manage");
  const canSecurity = hasAny("platform.security.view", "platform.security.manage");
  const canSupport = hasAny("platform.support.view", "platform.support.manage");
  const canHealth = has("platform.health.view");
  const canSettings = hasAny("platform.tenants.manage", "platform.security.manage");

  const categories: AdminCategory[] = [
    {
      id: "identity",
      label: "Identity & roles",
      show: canUsers || canRoles || canAssignRoles,
      items: [
        { to: `${BASE}/identity/members`,    label: "Members",                show: canUsers },
        { to: `${BASE}/identity/roles`,      label: "Roles & permissions",    show: canRoles },
        { to: `${BASE}/identity/user-roles`, label: "User role assignments",  show: canAssignRoles }
      ]
    },
    {
      id: "monetisation",
      label: "Monetisation",
      show: canPlans || canPayments || canBilling,
      items: [
        { to: `${BASE}/monetisation/plans`,            label: "Subscription plans", show: canPlans },
        { to: `${BASE}/monetisation/payment-gateways`, label: "Payment gateways",   show: canPayments },
        { to: `${BASE}/monetisation/billing`,          label: "Billing & refunds",  show: canBilling }
      ]
    },
    {
      id: "dhis2",
      label: "DHIS2 integration",
      show: canIntegrations,
      items: [
        { to: `${BASE}/dhis2/instances`,   label: "DHIS2 instances",   show: canIntegrations },
        { to: `${BASE}/dhis2/app-clients`, label: "DHIS2 app clients", show: canIntegrations },
        { to: `${BASE}/dhis2/forwarding`,  label: "Forwarding oversight", show: canIntegrations }
      ]
    },
    {
      id: "operations",
      label: "Platform operations",
      show: canSecrets || canAudit || canFeatureFlags || canSecurity || canSupport || canHealth,
      items: [
        { to: `${BASE}/operations/secrets`,         label: "Secrets inventory", show: canSecrets },
        { to: `${BASE}/operations/audit`,           label: "Audit log",         show: canAudit },
        { to: `${BASE}/operations/feature-flags`,   label: "Feature flags",     show: canFeatureFlags },
        { to: `${BASE}/operations/security-center`, label: "Security center",   show: canSecurity },
        { to: `${BASE}/operations/support`,         label: "Support console",   show: canSupport },
        { to: `${BASE}/operations/health`,          label: "Platform health",   show: canHealth }
      ]
    },
    {
      id: "settings",
      label: "Settings",
      show: canSettings,
      items: [
        { to: `${BASE}/settings/general`, label: "Platform settings", show: canSettings }
      ]
    }
  ];

  return (
    <AdministrationShell
      session={session}
      basePath={BASE}
      brandLabel="TallyStream · Platform administration"
      categories={categories}
      backTo={{ to: "/admin", label: "Back to platform overview" }}
    >
      <Routes>
        <Route index element={<Navigate to="identity/members" replace />} />
        <Route path="identity"             element={<Navigate to="identity/members" replace />} />
        <Route path="identity/members"     element={<AdminMembers />} />
        <Route path="identity/roles"       element={<AdminRoles />} />
        <Route path="identity/user-roles"  element={<AdminUserRoles />} />
        <Route path="monetisation"                  element={<Navigate to="monetisation/plans" replace />} />
        <Route path="monetisation/plans"            element={<AdminPlans />} />
        <Route path="monetisation/payment-gateways" element={<AdminPaymentGateways />} />
        <Route path="monetisation/billing"          element={<AdminBilling />} />
        <Route path="dhis2"             element={<Navigate to="dhis2/instances" replace />} />
        {/* DhisInstances handles list + create + edit + delete + secret
            rotation; it adapts to platform-admin context via isPlatformAdmin
            and adds a workspace selector to the create form. The legacy
            AdminInstances oversight view (cross-tenant connection-status
            roll-up + resync trigger) is still reachable below. */}
        <Route path="dhis2/instances"   element={<DhisInstances />} />
        <Route path="dhis2/instances/oversight" element={<AdminInstances />} />
        <Route path="dhis2/app-clients" element={<AdminAppClients />} />
        <Route path="dhis2/forwarding"  element={<AdminForwardingOversight />} />
        <Route path="dhis2/secrets"     element={<AdminSecrets />} />
        <Route path="operations"                  element={<Navigate to="operations/audit" replace />} />
        <Route path="operations/secrets"          element={<AdminSecrets />} />
        <Route path="operations/audit"            element={<AdminAudit />} />
        <Route path="operations/feature-flags"    element={<AdminFeatureFlags />} />
        <Route path="operations/security-center"  element={<AdminSecurityCenter />} />
        <Route path="operations/support"          element={<AdminSupportConsole />} />
        <Route path="operations/health"           element={<AdminHealth />} />
        <Route path="settings"         element={<Navigate to="settings/general" replace />} />
        <Route path="settings/general" element={<AdminSettings />} />
        {/* Catch-all renders the structured stub for nav items whose
            page hasn't shipped yet — keeps the IA fully reachable. */}
        <Route path="*" element={<ComingSoon />} />
      </Routes>
    </AdministrationShell>
  );
}
